import { getTableColumn, Select, TableColumn } from '@shared/UI';
import { useClientDetailsTable } from './ClientDetailsTableProvider';
import { getHeaderClassName } from '@shared/UI/SimpleTable/lib/getHeaderClassName';
import { HeaderCellWithFilter } from '@shared/UI/SimpleTable/components/HeaderCellWithFilter';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Drawer,
    IconButton,
} from '@mui/material';
import { ISettingsAccountsDto } from '@shared/api';
import { useState } from 'react';
import { deleteUser } from '../api';
import useStore from '@widgets/UserManagementTable/lib/store/assign-client-store';
import subAccountRefreshStore from '@widgets/UserManagementTable/lib/store/account-refresh-store';
import { FaRegUser, FaUserMinus, FaUserPlus } from 'react-icons/fa';
import { DrawerHeader } from '@features/Modal';
import { Steps } from '@features/Steps';
import CreateUserForm from '@widgets/UserManagementTable/components/UserManagementViewDrawer/components/CreateUser/CreateUser';
import '../ClientDetailsTable.scss';
import dropDownStore from '@widgets/ClientDetails/store/drop-down-table-store';
import snackbarStore from '@widgets/MainLayout/store/snackbar-store';
import { BiCaretDown, BiCaretUp } from 'react-icons/bi';
import { ReactComponent as BinIcon } from '../../../shared/assets/svgs/BinIcon.svg';

export function getTransactionsTableColumns(fromRIA: boolean): TableColumn<ISettingsAccountsDto>[] {
    const { desc, sortFieldName } = useClientDetailsTable();
    const [isLoading, setIsLoading] = useState(false);
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
    const setClientAssigned = useStore((state) => state.setClientAssigned);
    const setSubAccountRefresh = subAccountRefreshStore((state) => state.setSubAccountRefresh);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any | null>(null);

    const { setSnackbarMessage } = snackbarStore(); // Get the method to set the message

    const handleDrawerOpen = (row: any) => {
        setSelectedRow(row);
        setIsDrawerOpen(true);
    };

    const { rowsOpen } = dropDownStore((state) => state);

    const handleTableNested = (rowId: number) => {
        const { setRowOpen, rowsOpen } = dropDownStore.getState();
        // Toggle the open state for the specific row
        const newState = !rowsOpen[rowId]; // Invert the current state
        setRowOpen(rowId, newState);
    };

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
    };

    const AccountRoleMapping = {
        0: 'Unspecified',
        1: 'New',
        2: 'Sent to Clearing',
        3: 'Approved',
        4: 'Rejected',
        5: 'Restricted',
        6: 'Suspended',
        7: 'Closed',
    };

    const statusStyles = {
        Approved: { backgroundColor: '#D4F5E9', color: '#29A773' },
        Rejected: { backgroundColor: '#F5D4D4', color: '#D14545' },
        Restricted: { backgroundColor: '#F5D4D4', color: '#D14545' },
        Suspended: { backgroundColor: '#F5D4D4', color: '#D14545' },
        Closed: { backgroundColor: '#F5D4D4', color: '#D14545' },
        New: { backgroundColor: '#E4E9F7', color: '#5A78D5' },
        'Sent to Clearing': { backgroundColor: '#E4E9F7', color: '#5A78D5' },
        Unspecified: { backgroundColor: '#E4E4E4', color: '#A8A8A8' },
    };

    const handleOpenConfirmation = (rowId: number) => {
        setSelectedRowId(rowId);
        setConfirmationDialogOpen(true);
    };

    const handleCloseConfirmation = (event: any) => {
        setSelectedRowId(null);
        setConfirmationDialogOpen(false);
    };

    const columns: TableColumn<ISettingsAccountsDto>[] = [
        getTableColumn({
            dataField: 'id',
            flex: 0.001,
            firstColumn: true,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title='Account ID'
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            style: {
                visibility: 'collapse',
                width: 0,
                height: 0,
                padding: 0,
                margin: 0,
            } as React.CSSProperties,
            headerStyle: { display: 'none' } as React.CSSProperties,
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'id')}`,
        }),
        getTableColumn({
            dataField: 'clearingAccount',
            flex: 0.25,
            firstColumn: true,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title='CAN'
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'clearingAccount')}`,
        }),
        getTableColumn({
            dataField: 'verificationRequest.firstName',
            flex: 0.3,
            firstColumn: true,
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title='Account Name'
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'verificationRequest.firstName')}`,
            formatter: (cell, row) => `${row.verificationRequest.firstName} ${row.verificationRequest.lastName}`,
        }),
        getTableColumn({
            dataField: 'status',
            flex: 0.3,
            headerFormatter: () => (
                <HeaderCellWithFilter
                    title='Status'
                    icon={undefined}
                    onClick={() => null}
                />
            ),
            formatter: (cell) => {
                const text = AccountRoleMapping[cell as keyof typeof AccountRoleMapping] || 'Unknown';
                const style = statusStyles[text as keyof typeof statusStyles] || {
                    backgroundColor: '#E4E4E4',
                    color: '#000',
                };
                return (
                    <span
                        style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            backgroundColor: style.backgroundColor,
                            color: style.color,
                            fontWeight: 500,
                        }}
                    >
                        {text}
                    </span>
                );
            },
        }),
    ];

    if (!fromRIA) {
        columns.push(
            getTableColumn({
                dataField: 'Delete', // You can keep this as a placeholder, it won't affect the data
                flex: 0.3, // Adjust the flex value to fit your table layout
                headerFormatter: () => (
                    <HeaderCellWithFilter
                        title='Remove'
                        icon={undefined}
                        onClick={() => null}
                    />
                ),
                headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'Delete')}`,
                formatter: (_, row) => (
                    <>
                        <IconButton
                            onClick={(event) => {
                                event.stopPropagation(); // Prevent row click propagation
                                handleOpenConfirmation(row.id); // Pass the row ID
                            }}
                            style={{ padding: '0px' }}
                            color='primary'
                        >
                            <FaUserMinus />
                        </IconButton>
                        {confirmationDialogOpen && selectedRowId === row.id && (
                            <Dialog
                                open={confirmationDialogOpen}
                                onClose={handleCloseConfirmation}
                                aria-labelledby='confirm-dialog-title'
                                aria-describedby='confirm-dialog-description'
                            >
                                <div
                                    style={{
                                        width: '544px',
                                        height: '364px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    }}
                                >
                                    <div
                                        style={{
                                            backgroundColor: '#FFF6F6',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            width: '100px',
                                            height: '100px',
                                            borderRadius: '50%',
                                            alignSelf: 'center',
                                            marginTop: '30px',
                                        }}
                                    >
                                        <BinIcon />
                                    </div>
                                    <DialogTitle
                                        id='confirm-dialog-title'
                                        style={{
                                            alignSelf: 'center',
                                            fontWeight: '600',
                                            fontSize: '20px',
                                            paddingBottom: '5px',
                                        }}
                                    >
                                        Confirm Delete
                                    </DialogTitle>
                                    <DialogContent>
                                        <DialogContentText
                                            id='confirm-dialog-description'
                                            style={{
                                                paddingLeft: '32px',
                                                textAlignLast: 'center',
                                                paddingBottom: '30px',
                                                fontSize: '18px',
                                                color: '#09090B66',
                                            }}
                                        >
                                            Are you sure you want to unassign this account? This action cannot be
                                            undone.
                                        </DialogContentText>
                                    </DialogContent>
                                    <DialogActions
                                        style={{ marginLeft: 'auto', marginBottom: '10px', marginRight: '5px' }}
                                    >
                                        <Button
                                            onClick={(event) => {
                                                handleCloseConfirmation(event);
                                            }}
                                            color='primary'
                                            style={{
                                                background: 'white',
                                                color: 'black',
                                                border: '1.03px solid #E2E8F0',
                                                textTransform: 'none',
                                                fontSize: '14.36px',
                                                padding: 'revert-layer',
                                                borderRadius: '8px',
                                                width: '88px',
                                                height: '44px',
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={async (event: any) => {
                                                setIsLoading(true);
                                                try {
                                                    await deleteUser(selectedRowId); // Use the selectedRowId
                                                    setSnackbarMessage('Successfully Removed the Client Account');
                                                } catch {
                                                    setSnackbarMessage('Failed to Remove the Client Account');
                                                }
                                                handleCloseConfirmation(event);
                                                setClientAssigned(true);
                                                setSubAccountRefresh(true);
                                                setIsLoading(false);
                                            }}
                                            color='secondary'
                                            autoFocus
                                            disabled={isLoading}
                                            style={{
                                                background: '#0066A6',
                                                color: 'white',
                                                textTransform: 'none',
                                                fontSize: '14.36px',
                                                padding: 'revert-layer',
                                                borderRadius: '8px',
                                                width: '88px',
                                                height: '44px',
                                            }}
                                        >
                                            {isLoading ? <CircularProgress size={24} /> : 'Confirm'}
                                        </Button>
                                    </DialogActions>
                                </div>
                            </Dialog>
                        )}
                    </>
                ),
            }),
            getTableColumn({
                dataField: 'Assign User', // You can keep this as a placeholder, it won't affect the data
                flex: 0.2, // Adjust the flex value to fit your table layout
                headerFormatter: () => (
                    <HeaderCellWithFilter
                        title='Assign Client'
                        icon={undefined}
                        onClick={() => null}
                    />
                ),
                headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'Assign User')}`,
                formatter: (_, row) => (
                    <>
                        <IconButton
                            onClick={(event) => {
                                event.stopPropagation(); // Prevent row click propagation
                                handleDrawerOpen(row); // Pass the row ID
                            }}
                            style={{
                                color: '#0066a6', // Set icon color
                                backgroundColor: 'transparent', // Optional: make background transparent
                                borderRadius: '50%', // Optional: round shape for the button
                                paddingTop: '0px',
                            }}
                        >
                            <FaUserPlus />
                        </IconButton>
                        <div className='widget-transaction-view-drawer'>
                            <Drawer
                                anchor='right'
                                open={isDrawerOpen}
                                onClose={handleDrawerClose}
                                classes={{ paper: 'widget-transaction-view-drawer__paper' }}
                                sx={{
                                    '& .MuiDrawer-paper': {
                                        height: '97%',
                                        top: '14px',
                                        width: '500px',
                                        borderRadius: '15px',
                                        right: '10px',
                                    },
                                }}
                                BackdropProps={{
                                    sx: {
                                        backgroundColor: 'rgba(0, 0, 0, 0.2)', // Set the dimmed background color
                                    },
                                }}
                            >
                                <div className='widget-transaction-view-drawer__content'>
                                    <DrawerHeader
                                        headerTitle='Create User'
                                        headerIcon={<FaRegUser />}
                                    />
                                    <Steps
                                        footerClassName='widget-transaction-view-drawer__footer'
                                        headerClassName='widget-transaction-view-drawer__header_client'
                                        className='widget-transaction-view-drawer__steps_client'
                                        headerStepLabelClassName='widget-transaction-view-drawer__header-step-label'
                                        headerStepLineClassName='widget-transaction-view-drawer__header-step-line'
                                        footerVisible={false}
                                    >
                                        <CreateUserForm
                                            onCancel={handleDrawerClose}
                                            selectedAccount={selectedRow}
                                        />
                                    </Steps>
                                </div>
                            </Drawer>
                        </div>
                    </>
                ),
            }),
        );
    }

    if (fromRIA) {
        columns.push(
            getTableColumn({
                dataField: 'Delete', // You can keep this as a placeholder, it won't affect the data
                flex: 0.2, // Adjust the flex value to fit your table layout
                headerFormatter: () => (
                    <HeaderCellWithFilter
                        title='Assigned Securities'
                        icon={undefined}
                        onClick={() => null}
                    />
                ),
                headerClassName: `transactions-table-header ${getHeaderClassName(sortFieldName, desc, 'Delete')}`,
                formatter: (_, row) => {
                    const isRowOpen = rowsOpen[row.id]; // Check if the row is open
                    return (
                        <IconButton
                            onClick={(event) => {
                                handleTableNested(row.id); // Pass the row ID to toggle the open state
                            }}
                            color='primary'
                            style={{ padding: '0px' }}
                        >
                            {isRowOpen ? <BiCaretUp /> : <BiCaretDown />} {/* Toggle the icon */}
                        </IconButton>
                    );
                },
            }),
        );
    }

    return columns;
}
